import logger from './logger';

/**
 * Sends read markers with TIM reference client compliance.
 *
 * Reference client behavior:
 * - m.fully_read: ALWAYS sent (updates the "read up to" marker in the room)
 * - m.read.private: ALWAYS sent (private read receipt, only visible to self)
 * - m.read: CONDITIONAL based on receiptType setting (public read receipt)
 *
 * @param {MatrixClient} client - The Matrix client
 * @param {string} roomId - The room ID
 * @param {string} eventId - The event ID to mark as read (for m.fully_read)
 * @param {MatrixEvent} event - The Matrix event object (for m.read and m.read.private)
 * @param {string} receiptType - 'm.read' for public receipts, 'm.read.private' for private only
 * @returns {Promise<void>}
 */
export const sendReadMarkersWithCompliance = async (client, roomId, eventId, event, receiptType) => {
  if (!client || !roomId || !eventId || !event) {
    logger.warn('[readMarkerUtils] sendReadMarkersWithCompliance called with missing parameters', {
      hasClient: !!client,
      hasRoomId: !!roomId,
      hasEventId: !!eventId,
      hasEvent: !!event,
    });
    return;
  }

  // Determine if public receipts should be sent based on settings
  const sendPublicReceipt = receiptType === 'm.read';

  logger.debug('[readMarkerUtils] Sending read markers:', {
    roomId,
    eventId,
    sendPublicReceipt,
    receiptType,
  });

  // Call Matrix SDK API with proper types:
  // - rmEventId (string): m.fully_read marker - ALWAYS sent
  // - rrEvent (MatrixEvent): m.read public receipt - CONDITIONAL
  // - rpEvent (MatrixEvent): m.read.private receipt - ALWAYS sent
  await client.setRoomReadMarkers(
    roomId,
    eventId,                            // m.fully_read (always)
    sendPublicReceipt ? event : undefined, // m.read (conditional)
    event,                              // m.read.private (always)
  );

  logger.debug('[readMarkerUtils] Read markers sent successfully');
};

export default sendReadMarkersWithCompliance;
